import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/taskpane/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/taskpane/components/ui/tabs";
import { CircleDollarSign } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { useClipboard } from "@/hooks/useClipboard";
import { getReferralLink, sendInvitations, buildSocialShareUrl } from "./ReferUserDialog.utils";
import { useReferralEmails } from "@/taskpane/hooks/useReferralEmails";
import ReferralEmailForm from "./ReferralEmailForm";
import ReferralLinkForm from "./ReferralLinkForm";

interface ReferUserDialogProps extends React.ComponentPropsWithoutRef<typeof Dialog> {}

const ReferralInfoCard: React.FC = () => (
  <div className="space-y-3 mb-6">
    <div className="flex items-start gap-4 p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-300 text-neutral-white shrink-0">
        <CircleDollarSign size={16} />
      </div>
      <div>
        <h4 className="font-semibold text-xs">Give $600 & Get $600</h4>
        <p className="text-xs text-neutral-500">
          Give someone $100 credit to trial AI Legal Assistant and we&apos;ll give you $100 credit
          too. If they continue after the trial, we&apos;ll add an additional $500 credit to both
          accounts.
        </p>
      </div>
    </div>
  </div>
);

const ReferUserDialogHeader: React.FC = () => (
  <DialogHeader className="mb-4">
    <DialogTitle className="text-lg font-bold">Share the Love!</DialogTitle>
    <DialogDescription className="text-sm text-black font-medium">
      Earn up to $3,000 credit by sharing your experience with others.
    </DialogDescription>
  </DialogHeader>
);

const ReferUserDialog = ({ ...props }: ReferUserDialogProps) => {
  const { user } = useUser();
  const { copyToClipboard } = useClipboard();
  const referralLink = getReferralLink(user);
  const { emails, inputValue, isSending, setEmails, setInputValue, setIsSending, handleKeyDown } =
    useReferralEmails();

  const handleSendInvitations = () => {
    sendInvitations(emails, inputValue, setIsSending, setEmails, setInputValue, props.onOpenChange);
  };

  const handleCopyLink = () => {
    copyToClipboard(referralLink, "Referral link copied to clipboard!");
  };

  const handleSocialShare = (platform: "twitter" | "linkedin" | "facebook") => {
    window.open(buildSocialShareUrl(platform, referralLink), "_blank");
  };

  return (
    <Dialog {...props}>
      <DialogContent className="sm:max-w-[700px] p-0 gap-0 overflow-hidden">
        <div className="p-6 pb-2">
          <ReferUserDialogHeader />
          <ReferralInfoCard />
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 p-1.5 h-auto">
              <TabsTrigger value="email" className="text-xs py-1.5">
                Email Invitations
              </TabsTrigger>
              <TabsTrigger value="link" className="text-xs py-1.5">
                Share Link
              </TabsTrigger>
            </TabsList>
            <ReferralEmailForm
              emails={emails}
              inputValue={inputValue}
              isSending={isSending}
              onInputChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onRemoveEmail={(email) => setEmails(emails.filter((e) => e !== email))}
              onSendInvitations={handleSendInvitations}
            />
            <ReferralLinkForm
              referralLink={referralLink}
              onCopyLink={handleCopyLink}
              onSocialShare={handleSocialShare}
            />
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferUserDialog;
